<template>
  <DetailViewModal
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
    :title="questionTitle"
    :subtitle="questionSubtitle"
    icon="help-circle"
    icon-class="icon-question"
    size="lg"
    :loading="loading"
    :created-at="question?.createdAt"
    :updated-at="question?.updatedAt"
    :on-edit="onEdit"
    :item-id="question?.id"
    @close="$emit('close')"
  >
    <div v-if="question" class="question-view">
      <!-- Metadata Bar -->
      <div class="metadata-bar">
        <div class="metadata-item">
          <span class="metadata-label">Type</span>
          <span :class="['badge', getTypeBadge(question.question_type || question.type)]">
            {{ formatType(question.question_type || question.type) }}
          </span>
        </div>
        <div class="metadata-item">
          <span class="metadata-label">Difficulty</span>
          <span :class="['badge', getDifficultyBadge(question.difficulty)]">
            {{ question.difficulty || 'Medium' }}
          </span>
        </div>
        <div class="metadata-item">
          <span class="metadata-label">Marks</span>
          <span class="badge badge-marks">{{ question.marks || 1 }}</span>
        </div>
        <div v-if="question.isAiGenerated" class="metadata-item">
          <span class="badge bg-purple">
            <vue-feather type="cpu" size="12" />
            AI Generated
          </span>
        </div>
      </div>

      <!-- AI Translate Section -->
      <div class="translate-bar">
        <div class="translate-bar-left">
          <vue-feather type="globe" size="16" />
          <label class="translate-label">View in:</label>
          <select v-model="selectedLanguage" class="translate-select" @change="onLanguageChange">
            <option value="">Original</option>
            <option v-for="lang in languageOptions" :key="lang.code" :value="lang.code">{{ lang.label }}</option>
          </select>
        </div>
        <button v-if="selectedLanguage && !translatedContent" type="button" @click="fetchTranslation" class="translate-fetch-btn" :disabled="translating">
          <vue-feather :type="translating ? 'loader' : 'globe'" size="14" :class="{ 'spin': translating }" />
          {{ translating ? 'Translating...' : 'AI Translate' }}
        </button>
      </div>

      <!-- Translation Not Available Notice -->
      <div v-if="selectedLanguage && !translatedContent && !translating" class="translation-notice">
        <vue-feather type="info" size="16" />
        <span>Translation for this content is not available in {{ selectedLanguageLabel }}. Click "AI Translate" to generate one.</span>
      </div>

      <!-- Translating Indicator -->
      <div v-if="translating" class="translation-loading">
        <vue-feather type="loader" size="16" class="spin" />
        <span>Translating to {{ selectedLanguageLabel }}...</span>
      </div>

      <!-- Question Content -->
      <div class="content-section">
        <div class="section-header">
          <vue-feather type="help-circle" size="18" />
          <h4>Question</h4>
          <span v-if="selectedLanguage && translatedContent" class="translated-badge">
            <vue-feather type="globe" size="12" /> {{ selectedLanguageLabel }}
          </span>
        </div>
        <div class="section-content">
          <KatexContent :content="displayQuestionText" />
        </div>
      </div>

      <!-- Options (for MCQ) -->
      <div v-if="questionType === 'mcq' && displayOptions" class="content-section">
        <div class="section-header">
          <vue-feather type="list" size="18" />
          <h4>Options</h4>
        </div>
        <div class="options-grid">
          <div 
            v-for="(option, key) in displayOptions" 
            :key="key"
            :class="['option-card', { 'is-correct': isCorrectOption(key) }]"
          >
            <div class="option-label">{{ key.toUpperCase() }}</div>
            <div class="option-content">
              <KatexContent :content="option" />
            </div>
            <div v-if="isCorrectOption(key)" class="correct-indicator">
              <vue-feather type="check-circle" size="16" />
              Correct Answer
            </div>
          </div>
        </div>
      </div>

      <!-- Correct Answer (for non-MCQ or if MCQ doesn't have options) -->
      <div v-if="(questionType !== 'mcq' || !displayOptions) && correctAnswer" class="content-section">
        <div class="section-header">
          <vue-feather type="check-circle" size="18" />
          <h4>Correct Answer</h4>
        </div>
        <div class="section-content answer-content">
          <KatexContent :content="correctAnswer" />
        </div>
      </div>

      <!-- Explanation - Always show if available -->
      <div v-if="displayExplanation" class="content-section">
        <div class="section-header">
          <vue-feather type="info" size="18" />
          <h4>Explanation</h4>
        </div>
        <div class="section-content explanation-content">
          <KatexContent :content="displayExplanation" />
        </div>
      </div>

      <!-- Memory Hack -->
      <div v-if="displayMemoryHack" class="content-section memory-hack-section">
        <div class="section-header">
          <vue-feather type="cpu" size="18" />
          <h4>Memory Hack</h4>
        </div>
        <div class="section-content memory-hack-content">
          <KatexContent :content="displayMemoryHack" />
        </div>
      </div>

      <!-- Shortcut Method -->
      <div v-if="displayShortcutMethod" class="content-section shortcut-section">
        <div class="section-header">
          <vue-feather type="zap" size="18" />
          <h4>Shortcut Method</h4>
        </div>
        <div class="section-content shortcut-content">
          <KatexContent :content="displayShortcutMethod" />
        </div>
      </div>

      <!-- Trap Analysis -->
      <div v-if="displayTrapAnalysis" class="content-section trap-section">
        <div class="section-header">
          <vue-feather type="alert-triangle" size="18" />
          <h4>Trap Analysis</h4>
        </div>
        <div class="section-content trap-content">
          <KatexContent :content="displayTrapAnalysis" />
        </div>
      </div>

      <!-- Quick Tip -->
      <div v-if="quickTip" class="content-section tip-section">
        <div class="section-header">
          <vue-feather type="star" size="18" />
          <h4>Quick Tip</h4>
        </div>
        <div class="section-content tip-content">
          <KatexContent :content="quickTip" />
        </div>
      </div>

      <!-- Common Mistakes -->
      <div v-if="commonMistakes" class="content-section mistakes-section">
        <div class="section-header">
          <vue-feather type="x-circle" size="18" />
          <h4>Common Mistakes</h4>
        </div>
        <div class="section-content mistakes-content">
          <KatexContent :content="commonMistakes" />
        </div>
      </div>

      <!-- Assignment Info -->
      <div v-if="hasAssignment" class="content-section assignment-section">
        <div class="section-header">
          <vue-feather type="link" size="18" />
          <h4>Assignment</h4>
        </div>
        <div class="assignment-tags">
          <span v-if="question.examName" class="assignment-tag tag-exam">
            <vue-feather type="book-open" size="12" />
            {{ question.examName }}
          </span>
          <span v-if="question.subjectName" class="assignment-tag tag-subject">
            <vue-feather type="layers" size="12" />
            {{ question.subjectName }}
          </span>
          <span v-if="question.topicName" class="assignment-tag tag-topic">
            <vue-feather type="bookmark" size="12" />
            {{ question.topicName }}
          </span>
          <span v-if="question.lessonName" class="assignment-tag tag-lesson">
            <vue-feather type="file-text" size="12" />
            {{ question.lessonName }}
          </span>
          <span v-if="question.testName" class="assignment-tag tag-test">
            <vue-feather type="clipboard" size="12" />
            {{ question.testName }}
          </span>
        </div>
      </div>

      <!-- Attachments -->
      <div v-if="attachmentUrls.length" class="content-section attachments-section">
        <div class="section-header">
          <vue-feather type="paperclip" size="18" />
          <h4>Attachments</h4>
          <span class="badge bg-secondary">{{ attachmentUrls.length }}</span>
        </div>
        <div class="attachments-grid">
          <a
            v-for="(url, index) in attachmentUrls"
            :key="index"
            :href="url"
            target="_blank"
            rel="noopener noreferrer"
            class="attachment-card"
          >
            <img v-if="isImageUrl(url)" :src="url" class="attachment-preview" :alt="'Attachment ' + (index + 1)" />
            <div v-else class="attachment-file-placeholder">
              <vue-feather type="file" size="24" />
            </div>
            <div class="attachment-info">
              <span class="attachment-filename">{{ getAttachmentFilename(url) }}</span>
              <vue-feather type="external-link" size="12" class="attachment-link-icon" />
            </div>
          </a>
        </div>
      </div>
    </div>
  </DetailViewModal>
</template>

<script>
import { computed, ref, watch } from 'vue'
import DetailViewModal from './DetailViewModal.vue'
import KatexContent from '@/components/content/KatexContent.vue'
import api from '@/services/api'
import { languageOptions } from '@/utils/languageOptions'

export default {
  name: 'QuestionViewModal',
  components: { DetailViewModal, KatexContent },
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    question: {
      type: Object,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    onEdit: {
      type: Function,
      default: null
    }
  },
  emits: ['update:modelValue', 'close'],
  setup(props) {
    // Translation state
    const selectedLanguage = ref('')
    const translating = ref(false)
    const translatedContent = ref(null)

    const selectedLanguageLabel = computed(() => {
      const lang = languageOptions.find(l => l.code === selectedLanguage.value)
      return lang?.label || selectedLanguage.value
    })

    // Reset translation when question changes or modal closes
    watch(() => props.question, () => {
      selectedLanguage.value = ''
      translatedContent.value = null
    })

    watch(() => props.modelValue, (val) => {
      if (!val) {
        selectedLanguage.value = ''
        translatedContent.value = null
      }
    })

    const onLanguageChange = async () => {
      translatedContent.value = null
      if (!selectedLanguage.value || !props.question?.id) return
      translating.value = true
      try {
        const res = await api.get('/v1/admin/translations', {
          params: { content_type: 'question', content_id: props.question.id, language_code: selectedLanguage.value }
        })
        const translations = res.data?.translations || []
        if (translations.length > 0) {
          let td = translations[0].translated_data
          if (typeof td === 'string') { try { td = JSON.parse(td) } catch (e) { td = {} } }

          let opts = td.options
          if (typeof opts === 'string') { try { opts = JSON.parse(opts) } catch (e) { opts = {} } }
          let meta = td.answerMeta
          if (typeof meta === 'string') { try { meta = JSON.parse(meta) } catch (e) { meta = {} } }

          const parsed = {}
          if (td.questionText) parsed.question_text = td.questionText
          if (opts) {
            if (opts.a) parsed.option_a = opts.a
            if (opts.b) parsed.option_b = opts.b
            if (opts.c) parsed.option_c = opts.c
            if (opts.d) parsed.option_d = opts.d
          }
          if (meta) {
            if (meta.explanation) parsed.explanation = meta.explanation
            if (meta.shortcut_method) parsed.shortcut_method = meta.shortcut_method
            if (meta.memory_hack) parsed.memory_hack = meta.memory_hack
            if (meta.trap_analysis) parsed.trap_analysis = meta.trap_analysis
          }
          if (parsed.question_text || parsed.option_a || parsed.explanation) {
            translatedContent.value = parsed
          }
        }
      } catch (error) {
        console.error('Failed to fetch translation:', error)
      } finally {
        translating.value = false
      }
    }

    const fetchTranslation = async () => {
      if (!selectedLanguage.value || !props.question) return
      translating.value = true
      try {
        const fieldsToTranslate = {}
        const qText = props.question.questionText || props.question.content || props.question.text || ''
        if (qText) fieldsToTranslate.question_text = qText
        
        let opts = props.question.options
        if (typeof opts === 'string') { try { opts = JSON.parse(opts) } catch (e) { opts = null } }
        if (opts) {
          if (opts.a) fieldsToTranslate.option_a = opts.a
          if (opts.b) fieldsToTranslate.option_b = opts.b
          if (opts.c) fieldsToTranslate.option_c = opts.c
          if (opts.d) fieldsToTranslate.option_d = opts.d
        }

        const explanationText = props.question.explanation || ''
        if (explanationText) fieldsToTranslate.explanation = explanationText

        const shortcutText = props.question.shortcut_method || props.question.shortcutMethod || ''
        if (shortcutText) fieldsToTranslate.shortcut_method = shortcutText

        const memoryHackText = props.question.memory_hack || props.question.memoryHack || ''
        if (memoryHackText) fieldsToTranslate.memory_hack = memoryHackText

        const trapText = props.question.trap_analysis || props.question.trapAnalysis || ''
        if (trapText) fieldsToTranslate.trap_analysis = trapText

        const response = await api.post('/v1/admin/admin-chat', {
          message: `Translate the following educational question content to ${selectedLanguageLabel.value}. Keep mathematical formulas, LaTeX expressions, and technical terms intact. Return ONLY a valid JSON object with the same keys translated:\n\n${JSON.stringify(fieldsToTranslate, null, 2)}`,
          generation_type: 'translate',
          output_format: 'structured'
        })

        let aiData = response.data?.structured_data?.data || {}
        if (!aiData.question_text) {
          const responseText = response.data?.response || response.data?.message || ''
          const jsonMatch = responseText.match(/```(?:json)?\s*([\s\S]*?)```/)
          if (jsonMatch) { try { aiData = JSON.parse(jsonMatch[1].trim()) } catch (e) {} }
          if (!aiData.question_text) { try { aiData = JSON.parse(responseText) } catch (e) {} }
        }

        if (aiData.question_text || aiData.option_a || aiData.explanation) {
          translatedContent.value = aiData
        }
      } catch (error) {
        console.error('Translation failed:', error)
      } finally {
        translating.value = false
      }
    }

    // Display computed properties that use translated content when available
    const displayQuestionText = computed(() => {
      if (selectedLanguage.value && translatedContent.value?.question_text) {
        return translatedContent.value.question_text
      }
      return props.question?.questionText || props.question?.content || props.question?.text || ''
    })

    const displayOptions = computed(() => {
      if (selectedLanguage.value && translatedContent.value) {
        const tc = translatedContent.value
        if (tc.option_a || tc.option_b || tc.option_c || tc.option_d) {
          const origOpts = options.value || {}
          return {
            a: tc.option_a || origOpts.a || '',
            b: tc.option_b || origOpts.b || '',
            c: tc.option_c || origOpts.c || '',
            d: tc.option_d || origOpts.d || ''
          }
        }
      }
      return options.value
    })

    const displayExplanation = computed(() => {
      if (selectedLanguage.value && translatedContent.value?.explanation) {
        return translatedContent.value.explanation
      }
      return explanation.value
    })

    const displayMemoryHack = computed(() => {
      if (selectedLanguage.value && translatedContent.value?.memory_hack) {
        return translatedContent.value.memory_hack
      }
      return memoryHack.value
    })

    const displayShortcutMethod = computed(() => {
      if (selectedLanguage.value && translatedContent.value?.shortcut_method) {
        return translatedContent.value.shortcut_method
      }
      return shortcutMethod.value
    })

    const displayTrapAnalysis = computed(() => {
      if (selectedLanguage.value && translatedContent.value?.trap_analysis) {
        return translatedContent.value.trap_analysis
      }
      return trapAnalysis.value
    })
    // Parse answerMeta if it exists
    const answerMeta = computed(() => {
      if (!props.question?.answerMeta) return {}
      
      if (typeof props.question.answerMeta === 'string') {
        try {
          return JSON.parse(props.question.answerMeta)
        } catch (e) {
          console.error('Failed to parse answerMeta:', e)
          return {}
        }
      }
      
      return props.question.answerMeta
    })

    const questionType = computed(() => {
      return (props.question?.question_type || props.question?.type || '').toLowerCase()
    })

    const questionTitle = computed(() => {
      const text = props.question?.questionText || props.question?.content || props.question?.text || 'Question'
      return text.length > 80 ? text.substring(0, 80) + '...' : text
    })

    const questionSubtitle = computed(() => {
      const parts = []
      if (props.question?.topicName) parts.push(props.question.topicName)
      if (props.question?.lessonName) parts.push(props.question.lessonName)
      return parts.join(' • ')
    })

    const options = computed(() => {
      if (!props.question) return null
      
      // Try to parse options if it's a string
      let opts = props.question.options
      if (typeof opts === 'string') {
        try {
          opts = JSON.parse(opts)
        } catch (e) {
          return null
        }
      }
      
      return opts
    })

    const isCorrectOption = (key) => {
      // Check answerMeta first, then fallback to direct properties
      const correctAnswer = (
        answerMeta.value?.correct_answer || 
        props.question?.correct_answer || 
        props.question?.correctAnswer || 
        ''
      ).toLowerCase()
      return correctAnswer === key.toLowerCase()
    }

    const hasAssignment = computed(() => {
      return props.question?.examName || 
             props.question?.subjectName || 
             props.question?.topicName || 
             props.question?.lessonName ||
             props.question?.testName
    })

    const attachmentUrls = computed(() => {
      const urls = props.question?.attachmentUrls || props.question?.attachment_urls || []
      return Array.isArray(urls) ? urls : []
    })

    const getAttachmentFilename = (url) => {
      try {
        const pathname = new URL(url).pathname
        return pathname.split('/').pop() || url
      } catch {
        return url
      }
    }

    const isImageUrl = (url) => {
      return /\.(jpg|jpeg|png|gif|webp|svg|bmp)(\?.*)?$/i.test(url)
    }

    // Extract values from answerMeta or direct properties
    const correctAnswer = computed(() => {
      return answerMeta.value?.correct_answer || 
             props.question?.correct_answer || 
             props.question?.correctAnswer || 
             ''
    })

    const explanation = computed(() => {
      let explanationText = answerMeta.value?.explanation || 
             props.question?.explanation || 
             ''
      
      // Remove all special sections from explanation if they exist
      if (explanationText) {
        const sectionsToRemove = [
          /(?:\*\*)?(?:Memory\s*Hack)(?:\s*:|\*\*:?)\s*([\s\S]*?)(?=(?:\*\*)?(?:Shortcut|Trap|Quick\s*Tip|Common\s*Mistake|Pro\s*Tip)|$)/gi,
          /(?:\*\*)?(?:Shortcut\s*(?:Method)?)(?:\s*:|\*\*:?)\s*([\s\S]*?)(?=(?:\*\*)?(?:Memory|Trap|Quick\s*Tip|Common\s*Mistake|Pro\s*Tip)|$)/gi,
          /(?:\*\*)?(?:Trap\s*(?:Analysis)?)(?:\s*:|\*\*:?)\s*([\s\S]*?)(?=(?:\*\*)?(?:Memory|Shortcut|Quick\s*Tip|Common\s*Mistake|Pro\s*Tip)|$)/gi,
          /(?:\*\*)?(?:Quick\s*Tip|Pro\s*Tip|Tip)(?:\s*:|\*\*:?)\s*([\s\S]*?)(?=(?:\*\*)?(?:Memory|Shortcut|Trap|Common\s*Mistake)|$)/gi,
          /(?:\*\*)?(?:Common\s*Mistake[s]?)(?:\s*:|\*\*:?)\s*([\s\S]*?)$/gi
        ]
        
        sectionsToRemove.forEach(pattern => {
          explanationText = explanationText.replace(pattern, '')
        })
        
        explanationText = explanationText.trim()
      }
      
      return explanationText
    })

    // Helper to extract section from text
    const extractSection = (text, patterns) => {
      if (!text) return ''
      
      for (const pattern of patterns) {
        const match = text.match(pattern)
        if (match && match[1]) {
          return match[1].trim().replace(/^\*\*|\*\*$/g, '').trim()
        }
      }
      return ''
    }

    // Get full text to search from
    const fullText = computed(() => {
      return (answerMeta.value?.explanation || props.question?.explanation || '') + ' ' +
             (answerMeta.value?.shortcut_method || props.question?.shortcut_method || props.question?.shortcutMethod || '')
    })

    const memoryHack = computed(() => {
      // Check direct properties first
      const direct = answerMeta.value?.memory_hack || 
                     props.question?.memory_hack || 
                     props.question?.memoryHack || ''
      if (direct) return direct
      
      // Extract from text
      return extractSection(fullText.value, [
        /(?:\*\*)?Memory\s*Hack(?:\*\*)?(?:\s*:|\s*-)\s*([\s\S]*?)(?=(?:\n\n|\*\*(?:Shortcut|Trap|Quick|Common|Pro)|$))/i,
        /Memory\s*Hack[:\s]+([\s\S]*?)(?=(?:Shortcut|Trap|Quick|Common|Pro\s*Tip|\n\n\n|$))/i
      ])
    })

    const shortcutMethod = computed(() => {
      // Check direct properties first
      const direct = answerMeta.value?.shortcut_method || 
                     answerMeta.value?.shortcut ||
                     props.question?.shortcut_method || 
                     props.question?.shortcutMethod || ''
      if (direct) return direct
      
      // Extract from text
      return extractSection(fullText.value, [
        /(?:\*\*)?Shortcut(?:\s*Method)?(?:\*\*)?(?:\s*:|\s*-)\s*([\s\S]*?)(?=(?:\n\n|\*\*(?:Memory|Trap|Quick|Common|Pro)|$))/i,
        /Shortcut(?:\s*Method)?[:\s]+([\s\S]*?)(?=(?:Memory|Trap|Quick|Common|Pro\s*Tip|\n\n\n|$))/i
      ])
    })

    const trapAnalysis = computed(() => {
      // Check direct properties first
      const direct = answerMeta.value?.trap_analysis || 
                     answerMeta.value?.trap ||
                     props.question?.trap_analysis || 
                     props.question?.trapAnalysis || ''
      if (direct) return direct
      
      // Extract from text
      return extractSection(fullText.value, [
        /(?:\*\*)?Trap(?:\s*Analysis)?(?:\*\*)?(?:\s*:|\s*-)\s*([\s\S]*?)(?=(?:\n\n|\*\*(?:Memory|Shortcut|Quick|Common|Pro)|$))/i,
        /Trap(?:\s*Analysis)?[:\s]+([\s\S]*?)(?=(?:Memory|Shortcut|Quick|Common|Pro\s*Tip|\n\n\n|$))/i
      ])
    })

    const quickTip = computed(() => {
      // Check direct properties first
      const direct = answerMeta.value?.quick_tip || 
                     answerMeta.value?.pro_tip ||
                     answerMeta.value?.tip ||
                     props.question?.quick_tip || 
                     props.question?.quickTip ||
                     props.question?.pro_tip ||
                     props.question?.proTip || ''
      if (direct) return direct
      
      // Extract from text
      return extractSection(fullText.value, [
        /(?:\*\*)?(?:Quick\s*Tip|Pro\s*Tip|Tip)(?:\*\*)?(?:\s*:|\s*-)\s*([\s\S]*?)(?=(?:\n\n|\*\*(?:Memory|Shortcut|Trap|Common)|$))/i,
        /(?:Quick\s*Tip|Pro\s*Tip)[:\s]+([\s\S]*?)(?=(?:Memory|Shortcut|Trap|Common|\n\n\n|$))/i
      ])
    })

    const commonMistakes = computed(() => {
      // Check direct properties first
      const direct = answerMeta.value?.common_mistakes || 
                     answerMeta.value?.common_mistake ||
                     props.question?.common_mistakes || 
                     props.question?.commonMistakes || ''
      if (direct) return direct
      
      // Extract from text
      return extractSection(fullText.value, [
        /(?:\*\*)?Common\s*Mistake[s]?(?:\*\*)?(?:\s*:|\s*-)\s*([\s\S]*?)(?=(?:\n\n|\*\*(?:Memory|Shortcut|Trap|Quick|Pro)|$))/i,
        /Common\s*Mistake[s]?[:\s]+([\s\S]*?)$/i
      ])
    })

    const formatType = (type) => {
      if (!type) return '-'
      const types = { mcq: 'MCQ', numerical: 'Numerical', match: 'Match', subjective: 'Subjective' }
      return types[type.toLowerCase()] || type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())
    }

    const getTypeBadge = (type) => {
      if (!type) return 'bg-secondary'
      const badges = { mcq: 'bg-primary', numerical: 'bg-info', match: 'bg-warning', subjective: 'bg-secondary' }
      return badges[type.toLowerCase()] || 'bg-secondary'
    }

    const getDifficultyBadge = (difficulty) => {
      const badges = { easy: 'bg-success', medium: 'bg-warning', hard: 'bg-danger' }
      return badges[difficulty] || 'bg-secondary'
    }

    return {
      languageOptions,
      selectedLanguage,
      translating,
      translatedContent,
      selectedLanguageLabel,
      onLanguageChange,
      fetchTranslation,
      displayQuestionText,
      displayOptions,
      displayExplanation,
      displayMemoryHack,
      displayShortcutMethod,
      displayTrapAnalysis,
      questionType,
      questionTitle,
      questionSubtitle,
      options,
      isCorrectOption,
      hasAssignment,
      attachmentUrls,
      getAttachmentFilename,
      isImageUrl,
      correctAnswer,
      explanation,
      memoryHack,
      shortcutMethod,
      trapAnalysis,
      quickTip,
      commonMistakes,
      formatType,
      getTypeBadge,
      getDifficultyBadge
    }
  }
}
</script>

<style scoped>
.question-view {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

/* Metadata Bar */
.metadata-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 1rem;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border-radius: 12px;
  border: 1px solid #bae6fd;
}

/* Translate Bar */
.translate-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border-radius: 10px;
  border: 1px solid #7dd3fc;
}

.translate-bar-left {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #0369a1;
}

.translate-label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #0369a1;
}

.translate-select {
  padding: 0.375rem 0.625rem;
  border: 1px solid #bae6fd;
  border-radius: 6px;
  font-size: 0.8125rem;
  background: white;
  cursor: pointer;
  min-width: 140px;
}

.translate-select:focus {
  outline: none;
  border-color: #0ea5e9;
  box-shadow: 0 0 0 2px rgba(14, 165, 233, 0.15);
}

.translate-fetch-btn {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.875rem;
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.translate-fetch-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.4);
}

.translate-fetch-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.translated-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.125rem 0.5rem;
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  color: white;
  border-radius: 4px;
  font-size: 0.6875rem;
  font-weight: 600;
  margin-left: auto;
}

.translation-notice {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: #fef3c7;
  border-radius: 8px;
  border: 1px solid #fbbf24;
  color: #92400e;
  font-size: 0.8125rem;
}

.translation-loading {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: #e0f2fe;
  border-radius: 8px;
  border: 1px solid #7dd3fc;
  color: #0369a1;
  font-size: 0.8125rem;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.metadata-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.metadata-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.625rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.badge-marks {
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
  color: white;
}

.bg-primary { background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%); color: white; }
.bg-info { background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%); color: white; }
.bg-warning { background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); color: white; }
.bg-secondary { background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%); color: white; }
.bg-success { background: linear-gradient(135deg, #10b981 0%, #059669 100%); color: white; }
.bg-danger { background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%); color: white; }
.bg-purple { background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%); color: white; }

/* Content Sections */
.content-section {
  background: #f9fafb;
  border-radius: 12px;
  padding: 1.25rem;
  border: 1px solid #e5e7eb;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  margin-bottom: 1rem;
  color: #374151;
}

.section-header h4 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
}

.section-content {
  color: #1f2937;
  line-height: 1.7;
}

.question-text {
  font-size: 1.0625rem;
  font-weight: 500;
}

/* Options Grid */
.options-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
}

.option-card {
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  padding: 1rem;
  transition: all 0.2s;
  position: relative;
}

.option-card:hover {
  border-color: #d1d5db;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.option-card.is-correct {
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
  border-color: #10b981;
}

.option-label {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.875rem;
  margin-bottom: 0.75rem;
}

.option-card.is-correct .option-label {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.option-content {
  color: #374151;
  line-height: 1.6;
}

.correct-indicator {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #a7f3d0;
  color: #059669;
  font-size: 0.8125rem;
  font-weight: 600;
}

/* Answer Content */
.answer-content {
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid #a7f3d0;
}

/* Explanation Content */
.explanation-content {
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid #bfdbfe;
}

/* Shortcut Section */
.shortcut-section {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border-color: #fbbf24;
}

.shortcut-section .section-header {
  color: #92400e;
}

.shortcut-content {
  background: rgba(255, 255, 255, 0.7);
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid #fcd34d;
  color: #78350f;
}

/* Memory Hack Section */
.memory-hack-section {
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border-color: #86efac;
}

.memory-hack-section .section-header {
  color: #166534;
}

.memory-hack-content {
  background: rgba(255, 255, 255, 0.7);
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid #86efac;
  color: #166534;
}

/* Trap Analysis Section */
.trap-section {
  background: linear-gradient(135deg, #fef2f2 0%, #fecaca 100%);
  border-color: #f87171;
}

.trap-section .section-header {
  color: #991b1b;
}

.trap-content {
  background: rgba(255, 255, 255, 0.7);
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid #fca5a5;
  color: #991b1b;
}

/* Quick Tip Section */
.tip-section {
  background: linear-gradient(135deg, #fdf4ff 0%, #f5d0fe 100%);
  border-color: #e879f9;
}

.tip-section .section-header {
  color: #86198f;
}

.tip-content {
  background: rgba(255, 255, 255, 0.7);
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid #e879f9;
  color: #86198f;
}

/* Common Mistakes Section */
.mistakes-section {
  background: linear-gradient(135deg, #fff7ed 0%, #fed7aa 100%);
  border-color: #fb923c;
}

.mistakes-section .section-header {
  color: #9a3412;
}

.mistakes-content {
  background: rgba(255, 255, 255, 0.7);
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid #fdba74;
  color: #9a3412;
}

/* Assignment Section */
.assignment-section {
  background: linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%);
  border-color: #c4b5fd;
}

.assignment-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.625rem;
}

.assignment-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0.875rem;
  background: white;
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 500;
  border: 1px solid #e5e7eb;
}

.tag-exam { color: #7c3aed; border-color: #c4b5fd; }
.tag-subject { color: #2563eb; border-color: #bfdbfe; }
.tag-topic { color: #059669; border-color: #a7f3d0; }
.tag-lesson { color: #d97706; border-color: #fcd34d; }
.tag-test { color: #dc2626; border-color: #fecaca; }

/* Dark Mode */
:global(body.dark-mode) .metadata-bar {
  background: linear-gradient(135deg, #0c4a6e 0%, #075985 100%);
  border-color: #0369a1;
}

:global(body.dark-mode) .translate-bar {
  background: linear-gradient(135deg, #0c4a6e 0%, #075985 100%);
  border-color: #0369a1;
}

:global(body.dark-mode) .translate-bar-left { color: #7dd3fc; }
:global(body.dark-mode) .translate-label { color: #7dd3fc; }
:global(body.dark-mode) .translate-select { background: #1e293b; border-color: #334155; color: #f3f4f6; }
:global(body.dark-mode) .translation-notice { background: #78350f; border-color: #b45309; color: #fde68a; }
:global(body.dark-mode) .translation-loading { background: #0c4a6e; border-color: #0369a1; color: #7dd3fc; }

:global(body.dark-mode) .metadata-label {
  color: #94a3b8;
}

:global(body.dark-mode) .content-section {
  background: #0f172a;
  border-color: #334155;
}

:global(body.dark-mode) .section-header {
  color: #e5e7eb;
}

:global(body.dark-mode) .section-content {
  color: #d1d5db;
}

:global(body.dark-mode) .option-card {
  background: #1e293b;
  border-color: #334155;
}

:global(body.dark-mode) .option-card.is-correct {
  background: linear-gradient(135deg, #064e3b 0%, #065f46 100%);
  border-color: #059669;
}

:global(body.dark-mode) .option-content {
  color: #d1d5db;
}

:global(body.dark-mode) .correct-indicator {
  border-top-color: #065f46;
  color: #34d399;
}

:global(body.dark-mode) .answer-content {
  background: linear-gradient(135deg, #064e3b 0%, #065f46 100%);
  border-color: #059669;
}

:global(body.dark-mode) .explanation-content {
  background: linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%);
  border-color: #3b82f6;
}

:global(body.dark-mode) .shortcut-section {
  background: linear-gradient(135deg, #78350f 0%, #92400e 100%);
  border-color: #b45309;
}

:global(body.dark-mode) .shortcut-section .section-header {
  color: #fde68a;
}

:global(body.dark-mode) .shortcut-content {
  background: rgba(0, 0, 0, 0.3);
  border-color: #d97706;
  color: #fde68a;
}

:global(body.dark-mode) .memory-hack-section {
  background: linear-gradient(135deg, #14532d 0%, #166534 100%);
  border-color: #22c55e;
}

:global(body.dark-mode) .memory-hack-section .section-header {
  color: #bbf7d0;
}

:global(body.dark-mode) .memory-hack-content {
  background: rgba(0, 0, 0, 0.3);
  border-color: #22c55e;
  color: #bbf7d0;
}

:global(body.dark-mode) .trap-section {
  background: linear-gradient(135deg, #7f1d1d 0%, #991b1b 100%);
  border-color: #ef4444;
}

:global(body.dark-mode) .trap-section .section-header {
  color: #fecaca;
}

:global(body.dark-mode) .trap-content {
  background: rgba(0, 0, 0, 0.3);
  border-color: #ef4444;
  color: #fecaca;
}

:global(body.dark-mode) .tip-section {
  background: linear-gradient(135deg, #701a75 0%, #86198f 100%);
  border-color: #d946ef;
}

:global(body.dark-mode) .tip-section .section-header {
  color: #f5d0fe;
}

:global(body.dark-mode) .tip-content {
  background: rgba(0, 0, 0, 0.3);
  border-color: #d946ef;
  color: #f5d0fe;
}

:global(body.dark-mode) .mistakes-section {
  background: linear-gradient(135deg, #7c2d12 0%, #9a3412 100%);
  border-color: #f97316;
}

:global(body.dark-mode) .mistakes-section .section-header {
  color: #fed7aa;
}

:global(body.dark-mode) .mistakes-content {
  background: rgba(0, 0, 0, 0.3);
  border-color: #f97316;
  color: #fed7aa;
}

:global(body.dark-mode) .assignment-section {
  background: linear-gradient(135deg, #4c1d95 0%, #5b21b6 100%);
  border-color: #7c3aed;
}

:global(body.dark-mode) .assignment-tag {
  background: #1e293b;
  border-color: #475569;
}

/* Attachments */
.attachments-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 0.75rem;
}

.attachment-card {
  display: flex;
  flex-direction: column;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
  text-decoration: none;
  color: inherit;
  transition: all 0.2s;
  background: white;
}

.attachment-card:hover {
  border-color: #6366f1;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.15);
  transform: translateY(-2px);
}

.attachment-preview {
  width: 100%;
  height: 120px;
  object-fit: cover;
}

.attachment-file-placeholder {
  width: 100%;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3f4f6;
  color: #9ca3af;
}

.attachment-info {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0.75rem;
  border-top: 1px solid #e5e7eb;
}

.attachment-filename {
  flex: 1;
  font-size: 0.75rem;
  color: #374151;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.attachment-link-icon {
  color: #9ca3af;
  flex-shrink: 0;
}

:global(body.dark-mode) .attachment-card {
  background: #1e293b;
  border-color: #374151;
}

:global(body.dark-mode) .attachment-card:hover {
  border-color: #818cf8;
}

:global(body.dark-mode) .attachment-file-placeholder {
  background: #0f172a;
  color: #6b7280;
}

:global(body.dark-mode) .attachment-info {
  border-color: #374151;
}

:global(body.dark-mode) .attachment-filename {
  color: #d1d5db;
}
</style>
